import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuAward,
  LuBookOpen,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuCircleAlert,
  LuCircleCheck,
  LuCircleHelp,
  LuCircleX,
  LuFastForward,
  LuGraduationCap,
  LuInfo,
  LuList,
  LuLock,
  LuMaximize,
  LuMinimize,
  LuPause,
  LuPlay,
  LuRewind,
  LuRotateCcw,
  LuVolume2,
  LuVolumeX,
} from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import QuizChoice from '../../components/PoolQuiz/QuizChoice';
import { db, auth } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useSessionHold from '../../hooks/useSessionHold';
import { useTranslation } from '../../i18n/LanguageContext';
import { QUIZ_SOURCE, drawQuizQuestions, gradeQuiz, recordQuizAttempt } from '../../utils/questionPool';
import { LearningProgress, PlayOverlay, VideoButton, VolumeControl } from './education/VideoParts';

/**
 * Endikasyon eğitimi (video): ileri sarma yalnızca izlenen kısma kadar, belirli
 * anlarda pop quiz, sonunda eğitimin kendi soruları + departman soru havuzundan
 * final sınavı ve geçilince sertifika. İlerleme userEducationProgress'te.
 * İşleyiş aynı; pop quiz ve sınav kapatılamayan pencerelerde açılır (eskisi gibi).
 */
const EducationPlayer = ({ show, onHide, education, onComplete }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.education.${key}`, vars);
  const { employeeData, employeeId } = useUserProfile();
  const userGroup = employeeData?.role;
  // Video refs
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const playerContainerRef = useRef(null);
  
  // Video state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [videoError, setVideoError] = useState(false);
  
  // Progress state
  const [watchedProgress, setWatchedProgress] = useState(0);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [lastPosition, setLastPosition] = useState(0);
  // Video izlenirken sayfaya olay gelmez; oynadıkça hareketsizlik çıkışını uzat
  useSessionHold(show && isPlaying);
  
  // Chapter state
  const [activeChapter, setActiveChapter] = useState(0);
  const [showChapters, setShowChapters] = useState(true);
  
  // Pop quiz state
  const [showPopQuiz, setShowPopQuiz] = useState(false);
  const [currentPopQuiz, setCurrentPopQuiz] = useState(null);
  const [completedPopQuizzes, setCompletedPopQuizzes] = useState(new Set());
  const [popQuizAnswer, setPopQuizAnswer] = useState(null);
  const [popQuizResult, setPopQuizResult] = useState(null);
  
  // Final quiz state
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [finalQuizAnswers, setFinalQuizAnswers] = useState({});
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [passed, setPassed] = useState(false);
  // Sınav soruları: eğitimin kendi final soruları + departman soru havuzundan
  // her denemede yeniden çekilen rastgele sorular (Kalite Yönetimi › Soru Havuzu)
  const [examQuestions, setExamQuestions] = useState([]);
  const [examPassScore, setExamPassScore] = useState(70);
  const examQuestionsRef = useRef([]);

  const prepareExam = useCallback(async () => {
    const ownQuestions = education?.finalQuiz || [];
    let poolQuestions = [];
    let poolPassScore = 70;
    try {
      const drawn = await drawQuizQuestions(userGroup);
      // Havuz kimlikleri eğitimin kendi soru kimlikleriyle çakışmasın
      poolQuestions = drawn.questions.map((question) => ({ ...question, id: `pool_${question.id}` }));
      poolPassScore = drawn.passScore;
    } catch (error) {
      // Havuz okunamazsa eğitimin kendi sorularıyla devam edilir
      console.error('Soru havuzu yüklenemedi:', error);
    }
    const combined = [...ownQuestions, ...poolQuestions];
    examQuestionsRef.current = combined;
    setExamQuestions(combined);
    setExamPassScore(education?.passScore || poolPassScore);
  }, [education, userGroup]);

  useEffect(() => {
    if (show && education) prepareExam();
  }, [show, education, prepareExam]);
  
  // Certificate state
  const [certificateEarned, setCertificateEarned] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Format time helper
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Load user progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!auth.currentUser || !education) return;
      
      try {
        setIsLoading(true);
        const progressRef = doc(db, 'userEducationProgress', `${auth.currentUser.uid}_${education.id}`);
        const progressDoc = await getDoc(progressRef);
        
        if (progressDoc.exists()) {
          const data = progressDoc.data();
          setLastPosition(data.lastPosition || 0);
          setMaxWatchedTime(data.maxWatchedTime || 0);
          setWatchedProgress(data.watchedProgress || 0);
          setCompletedPopQuizzes(new Set(data.completedPopQuizzes || []));
          setCertificateEarned(data.certificateEarned || false);
          
          if (data.certificateEarned) {
            setPassed(true);
            setFinalQuizScore(data.finalQuizScore || 0);
          }
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (show && education) {
      loadProgress();
    }
  }, [show, education]);

  // Save progress to Firestore
  const saveProgress = useCallback(async (additionalData = {}) => {
    if (!auth.currentUser || !education) return;
    
    try {
      const progressRef = doc(db, 'userEducationProgress', `${auth.currentUser.uid}_${education.id}`);
      await setDoc(progressRef, {
        userId: auth.currentUser.uid,
        educationId: education.id,
        educationTitle: education.title,
        lastPosition: currentTime,
        maxWatchedTime: Math.max(maxWatchedTime, currentTime),
        watchedProgress: watchedProgress,
        completedPopQuizzes: Array.from(completedPopQuizzes),
        certificateEarned: certificateEarned,
        finalQuizScore: finalQuizScore,
        passed: passed,
        lastUpdated: serverTimestamp(),
        ...additionalData
      }, { merge: true });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentTime, maxWatchedTime, watchedProgress, completedPopQuizzes, certificateEarned, finalQuizScore, passed, education]);

  // Refs to track high-frequency values without causing re-renders
  const maxWatchedTimeRef = useRef(maxWatchedTime);
  const lastUIUpdateRef = useRef(0);

  // Keep ref in sync when state changes from other sources
  useEffect(() => {
    maxWatchedTimeRef.current = maxWatchedTime;
  }, [maxWatchedTime]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const time = video.currentTime;
      const now = Date.now();

      // Update max watched time via ref (no re-render)
      if (time > maxWatchedTimeRef.current) {
        maxWatchedTimeRef.current = time;
      }

      // Check for pop quizzes on every tick (important - must not miss)
      if (education?.popQuizzes && !showPopQuiz) {
        const popQuiz = education.popQuizzes.find(quiz => {
          const quizTime = quiz.timestamp;
          return Math.abs(time - quizTime) < 0.5 && !completedPopQuizzes.has(quiz.id);
        });

        if (popQuiz) {
          video.pause();
          setIsPlaying(false);
          setCurrentPopQuiz(popQuiz);
          setShowPopQuiz(true);
          setPopQuizAnswer(null);
          setPopQuizResult(null);
          return;
        }
      }

      // Throttle UI state updates to ~4 times per second (every 250ms)
      if (now - lastUIUpdateRef.current < 250) return;
      lastUIUpdateRef.current = now;

      setCurrentTime(time);

      // Sync max watched time to state
      if (maxWatchedTimeRef.current > maxWatchedTime) {
        setMaxWatchedTime(maxWatchedTimeRef.current);
      }

      // Calculate progress percentage
      const progress = Math.floor((Math.max(maxWatchedTimeRef.current, time) / video.duration) * 100);
      setWatchedProgress(progress);

      // Update active chapter
      if (education?.chapters) {
        const chapterIndex = education.chapters.findIndex(
          ch => time >= ch.startTime && time < ch.endTime
        );
        if (chapterIndex !== -1) {
          setActiveChapter(chapterIndex);
        }
      }
    };
    
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      
      // Resume from last position if available
      if (lastPosition > 0 && lastPosition < video.duration - 5) {
        video.currentTime = lastPosition;
        toast.success(t('profilePage.education.toast.resumed', { time: formatTime(lastPosition) }));
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setWatchedProgress(100);
      saveProgress({ watchedProgress: 100 });
      
      // Show final quiz if available and not already passed
      if (examQuestionsRef.current.length > 0 && !certificateEarned) {
        setTimeout(() => {
          setShowFinalQuiz(true);
        }, 1000);
      }
    };
    
    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBuffered((bufferedEnd / video.duration) * 100);
      }
    };
    
    const handleError = () => {
      setVideoError(true);
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('error', handleError);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('error', handleError);
    };
  }, [education, showPopQuiz, completedPopQuizzes, lastPosition, maxWatchedTime, certificateEarned, saveProgress, t]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying) {
        saveProgress();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isPlaying, saveProgress]);

  // Save progress on unmount
  useEffect(() => {
    return () => {
      if (currentTime > 0) {
        saveProgress();
      }
    };
  }, [currentTime, saveProgress]);

  // Video controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!progressBarRef.current || !videoRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const newTime = position * duration;
    
    // Only allow seeking to already watched content (prevent skipping)
    if (newTime <= maxWatchedTime || newTime <= currentTime) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    } else {
      toast.error(label('toast.noSkip'));
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    
    if (isMuted) {
      videoRef.current.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      videoRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const skipForward = () => {
    if (!videoRef.current) return;
    const newTime = Math.min(currentTime + 10, maxWatchedTime);
    videoRef.current.currentTime = newTime;
  };

  const skipBackward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(currentTime - 10, 0);
  };

  const seekToChapter = (chapter) => {
    if (!videoRef.current) return;
    
    if (chapter.startTime <= maxWatchedTime) {
      videoRef.current.currentTime = chapter.startTime;
      setCurrentTime(chapter.startTime);
    } else {
      toast.error(label('toast.chapterLocked'));
    }
  };

  const toggleFullscreen = () => {
    const container = playerContainerRef.current;
    if (!container) return;
    
    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Pop quiz handlers
  const handlePopQuizAnswer = (answerIndex) => {
    setPopQuizAnswer(answerIndex);
  };

  const submitPopQuizAnswer = () => {
    if (popQuizAnswer === null || !currentPopQuiz) return;
    
    const isCorrect = popQuizAnswer === currentPopQuiz.correctAnswer;
    setPopQuizResult(isCorrect);
    
    if (isCorrect) {
      setCompletedPopQuizzes(prev => new Set([...prev, currentPopQuiz.id]));
    }
  };

  const continueAfterPopQuiz = () => {
    if (!popQuizResult && currentPopQuiz) {
      // Wrong answer - restart from 10 seconds before the quiz
      const restartTime = Math.max(0, currentPopQuiz.timestamp - 10);
      if (videoRef.current) {
        videoRef.current.currentTime = restartTime;
      }
      toast.error(label('toast.wrongAnswer'));
    }
    
    setShowPopQuiz(false);
    setCurrentPopQuiz(null);
    setPopQuizAnswer(null);
    setPopQuizResult(null);
    
    // Resume video
    if (videoRef.current && popQuizResult) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Final quiz handlers
  const handleFinalQuizAnswer = (questionId, answerIndex) => {
    setFinalQuizAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const submitFinalQuiz = async () => {
    if (examQuestions.length === 0) return;

    const { score, correctCount, passed: hasPassed } = gradeQuiz(examQuestions, finalQuizAnswers, examPassScore);
    recordQuizAttempt({
      userId: auth.currentUser?.uid || '',
      employeeId: employeeId || '',
      employeeName: `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim(),
      group: employeeData?.role || '',
      sourceType: QUIZ_SOURCE.EDUCATION,
      sourceId: education?.id || '',
      sourceName: education?.title || '',
      questionIds: examQuestions.map((question) => question.id),
      questionCount: examQuestions.length,
      correctCount,
      score,
      passScore: examPassScore,
      passed: hasPassed,
    });
    
    setFinalQuizScore(score);
    setPassed(hasPassed);
    setShowFinalResults(true);
    
    if (hasPassed) {
      setCertificateEarned(true);
      await saveProgress({
        certificateEarned: true,
        finalQuizScore: score,
        passed: true,
        completedAt: serverTimestamp()
      });
      toast.success(label('toast.certificate'));
    }
  };

  const retakeFinalQuiz = () => {
    // Her denemede havuzdan yeni sorular çekilir
    prepareExam();
    setFinalQuizAnswers({});
    setCurrentQuestionIndex(0);
    setShowFinalResults(false);
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    saveProgress();
    onHide();
  };

  const handleCompleteEducation = () => {
    if (onComplete) {
      onComplete(education.id);
    }
    handleClose();
  };

  if (!show) return null;

  // Check if education deadline has passed
  const isExpired = education?.hasDeadline && education?.deadline && new Date(education.deadline) < new Date();

  if (isExpired) {
    return (
      <Modal
        isOpen
        onClose={onHide}
        size="md"
        title={label('expiredTitle')}
        subtitle={education?.title}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30" size="xs">
            <LuCircleAlert aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={<Button variant="primary" onClick={onHide}>{t('common.close')}</Button>}
      >
        <div className="tw-space-y-2 tw-text-center">
          <p className="tw-text-[15px] tw-text-slate-700 dark:tw-text-slate-200">{label('expiredText')}</p>
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            {label('expiredDeadline', { date: new Date(education.deadline).toLocaleDateString(locale) })}
          </p>
        </div>
      </Modal>
    );
  }

  const percentOf = (time) => (duration ? (time / duration) * 100 : 0);
  const finalQuestion = examQuestions[currentQuestionIndex];

  const popQuizState = (index) => {
    if (popQuizResult !== null) {
      if (index === currentPopQuiz?.correctAnswer) return 'correct';
      if (popQuizAnswer === index && !popQuizResult) return 'wrong';
      return 'idle';
    }
    return popQuizAnswer === index ? 'selected' : 'idle';
  };

  return (
    <>
      <Modal
        isOpen
        onClose={handleClose}
        size="xl"
        title={education?.title || label('title')}
        subtitle={[education?.instructor, duration ? formatTime(duration) : null].filter(Boolean).join(' · ') || undefined}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30" size="xs">
            <LuGraduationCap aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={<Badge tone={watchedProgress >= 95 ? 'success' : 'info'}>{label('watchedBadge', { value: watchedProgress })}</Badge>}
      >
        <div className={`tw-grid tw-gap-5 ${showChapters ? 'lg:tw-grid-cols-[minmax(0,1fr)_20rem]' : ''}`}>
          {/* Video */}
          <div ref={playerContainerRef} className="tw-min-w-0 tw-overflow-hidden tw-rounded-2xl tw-bg-black tw-shadow-lg">
            <div className="tw-relative tw-aspect-video">
              {videoError ? (
                <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-6 tw-text-center tw-text-white">
                  <LuCircleAlert aria-hidden="true" className="tw-h-12 tw-w-12 tw-text-rose-400" />
                  <p className="tw-text-sm">{label('videoError')}</p>
                </div>
              ) : (
                <>
                  <video ref={videoRef} src={education?.videoUrl} onClick={togglePlay} className="tw-h-full tw-w-full tw-bg-black" />
                  {!isPlaying && <PlayOverlay label={label('play')} onClick={togglePlay} />}
                </>
              )}
            </div>

            <div className="tw-space-y-1 tw-bg-gradient-to-b tw-from-slate-900 tw-to-black tw-px-3 tw-pb-2 tw-pt-3">
              {/* İlerleme çubuğu: tampon, izlenen kısım, konum, pop quiz ve bölüm işaretleri */}
              <div ref={progressBarRef} onClick={handleSeek} role="presentation" className="tw-relative tw-h-4 tw-cursor-pointer">
                <span className="tw-absolute tw-inset-x-0 tw-top-1/2 tw-h-1.5 -tw-translate-y-1/2 tw-overflow-hidden tw-rounded-full tw-bg-white/15">
                  <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-white/20" style={{ width: `${buffered}%` }} />
                  <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-teal-400/35" style={{ width: `${percentOf(maxWatchedTime)}%` }} />
                  <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-teal-400" style={{ width: `${percentOf(currentTime)}%` }} />
                </span>
                {education?.chapters?.map((chapter) => (
                  <span key={chapter.id} title={chapter.title} style={{ left: `${percentOf(chapter.startTime)}%` }} className="tw-absolute tw-top-1/2 tw-h-2.5 tw-w-0.5 -tw-translate-y-1/2 tw-bg-white/60" />
                ))}
                {education?.popQuizzes?.map((quiz) => (
                  <span
                    key={quiz.id}
                    title={`${label('popQuizTitle')} · ${formatTime(quiz.timestamp)}`}
                    style={{ left: `${percentOf(quiz.timestamp)}%` }}
                    className={`tw-absolute tw-top-1/2 tw-h-2.5 tw-w-2.5 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rotate-45 tw-rounded-sm tw-ring-2 tw-ring-black ${completedPopQuizzes.has(quiz.id) ? 'tw-bg-emerald-400' : 'tw-bg-amber-400'}`}
                  />
                ))}
                <span className="tw-absolute tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-white tw-shadow" style={{ left: `${percentOf(currentTime)}%` }} />
              </div>

              <div className="tw-flex tw-items-center tw-gap-0.5">
                <VideoButton label={isPlaying ? label('pause') : label('play')} onClick={togglePlay}>
                  {isPlaying ? <LuPause aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuPlay aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
                </VideoButton>
                <VideoButton label={label('back10')} onClick={skipBackward}><LuRewind aria-hidden="true" className="tw-h-4 tw-w-4" /></VideoButton>
                <VideoButton label={label('forward10')} onClick={skipForward}><LuFastForward aria-hidden="true" className="tw-h-4 tw-w-4" /></VideoButton>
                <VolumeControl
                  muted={isMuted}
                  volume={volume}
                  onToggleMute={toggleMute}
                  onChange={handleVolumeChange}
                  muteLabel={label('mute')}
                  unmuteLabel={label('unmute')}
                  volumeLabel={label('volume')}
                  icon={LuVolume2}
                  mutedIcon={LuVolumeX}
                />
                <span className="tw-ml-2 tw-text-xs tw-font-medium tw-tabular-nums tw-text-white/80">{formatTime(currentTime)} / {formatTime(duration)}</span>
                <span className="tw-flex-1" />
                <VideoButton label={showChapters ? label('hideChapters') : label('showChapters')} onClick={() => setShowChapters(!showChapters)}>
                  <LuList aria-hidden="true" className="tw-h-4 tw-w-4" />
                </VideoButton>
                <VideoButton label={isFullscreen ? label('exitFullscreen') : label('fullscreen')} onClick={toggleFullscreen}>
                  {isFullscreen ? <LuMinimize aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuMaximize aria-hidden="true" className="tw-h-4 tw-w-4" />}
                </VideoButton>
              </div>
            </div>
          </div>

          {/* Bölümler ve sınav durumu */}
          {showChapters && (
            <aside className="tw-min-w-0 tw-space-y-4">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                  <LuList aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
                  {label('contents')}
                </p>
                <span className="tw-relative tw-grid tw-h-12 tw-w-12 tw-place-items-center">
                  <svg viewBox="0 0 36 36" className="tw-absolute tw-inset-0 tw-h-12 tw-w-12 -tw-rotate-90" aria-hidden="true">
                    <circle cx="18" cy="18" r="15.9155" fill="none" strokeWidth="3" className="tw-stroke-slate-200 dark:tw-stroke-white/10" />
                    <circle cx="18" cy="18" r="15.9155" fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${watchedProgress}, 100`} className="tw-stroke-teal-500" />
                  </svg>
                  <span className="tw-text-[11px] tw-font-bold tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{watchedProgress}%</span>
                </span>
              </div>

              <ol className="tw-space-y-1.5">
                {education?.chapters?.map((chapter, index) => {
                  const unlocked = chapter.startTime <= maxWatchedTime;
                  return (
                    <li key={chapter.id}>
                      <button
                        type="button"
                        onClick={() => seekToChapter(chapter)}
                        className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                          activeChapter === index
                            ? 'tw-border-teal-500/40 tw-bg-teal-500/10'
                            : 'tw-border-slate-200 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                        } ${unlocked ? '' : 'tw-opacity-60'}`}
                      >
                        <span className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-xs tw-font-bold ${activeChapter === index ? 'tw-bg-teal-500 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300'}`}>
                          {unlocked ? index + 1 : <LuLock aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                        </span>
                        <span className="tw-min-w-0 tw-flex-1">
                          <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{chapter.title}</span>
                          <span className="tw-block tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{formatTime(chapter.startTime)} – {formatTime(chapter.endTime)}</span>
                        </span>
                        {unlocked && <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-500" />}
                      </button>
                    </li>
                  );
                })}
              </ol>
              {!education?.chapters?.length && <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('noChapters')}</p>}

              {examQuestions.length > 0 && (
                <div className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-violet-500/25 tw-bg-violet-500/[0.06] tw-p-4">
                  <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-violet-900 dark:tw-text-violet-100">
                    <LuAward aria-hidden="true" className="tw-h-4 tw-w-4" />
                    {label('finalQuiz')}
                  </p>
                  {certificateEarned ? (
                    <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-emerald-700 dark:tw-text-emerald-300">
                      <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
                      {label('finalDone', { score: finalQuizScore })}
                    </p>
                  ) : watchedProgress >= 95 ? (
                    <Button variant="primary" size="sm" onClick={() => setShowFinalQuiz(true)}>{label('startExam')}</Button>
                  ) : (
                    <p className="tw-text-xs tw-text-violet-900/80 dark:tw-text-violet-100/80">{label('finalLocked')}</p>
                  )}
                </div>
              )}

              <LearningProgress label={label('watched')} percent={watchedProgress} valueText={`${watchedProgress}%`} />
              {isLoading && <p className="tw-text-xs tw-text-slate-400">{label('loadingProgress')}</p>}
            </aside>
          )}
        </div>
      </Modal>

      {/* Pop quiz: doğru cevaplanmadan devam edilmez */}
      <Modal
        isOpen={showPopQuiz}
        dismissible={false}
        onClose={() => {}}
        size="md"
        title={label('popQuizTitle')}
        subtitle={label('popQuizSubtitle')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
            <LuCircleHelp aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={popQuizResult === null ? (
          <Button variant="primary" onClick={submitPopQuizAnswer} disabled={popQuizAnswer === null} className="max-sm:tw-flex-1">{label('submitAnswer')}</Button>
        ) : (
          <Button variant={popQuizResult ? 'success' : 'warning'} onClick={continueAfterPopQuiz} className="max-sm:tw-flex-1">
            {popQuizResult ? <>{label('continue')}<LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" /></> : <><LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />{label('rewatch')}</>}
          </Button>
        )}
      >
        <div className="tw-space-y-4">
          <p className="tw-text-base tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{currentPopQuiz?.question}</p>
          <div role="radiogroup" aria-label={label('choices')} className="tw-space-y-2">
            {currentPopQuiz?.options.map((option, index) => (
              <QuizChoice
                key={index}
                letter={String.fromCharCode(65 + index)}
                state={popQuizState(index)}
                disabled={popQuizResult !== null}
                onClick={() => !popQuizResult && handlePopQuizAnswer(index)}
              >
                {option}
              </QuizChoice>
            ))}
          </div>
          {popQuizResult !== null && currentPopQuiz?.explanation && (
            <p className={`tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-px-3.5 tw-py-3 tw-text-sm ${popQuizResult ? 'tw-bg-emerald-500/10 tw-text-emerald-900 dark:tw-text-emerald-100' : 'tw-bg-rose-500/10 tw-text-rose-900 dark:tw-text-rose-100'}`}>
              <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
              <span>{currentPopQuiz.explanation}</span>
            </p>
          )}
        </div>
      </Modal>

      {/* Final sınavı */}
      <Modal
        isOpen={showFinalQuiz}
        dismissible={false}
        onClose={() => {}}
        size="lg"
        title={label('finalQuiz')}
        subtitle={label('passScore', { score: examPassScore })}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
            <LuAward aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={!showFinalResults ? (
          <>
            <Button variant="ghost" onClick={() => setCurrentQuestionIndex((prev) => prev - 1)} disabled={currentQuestionIndex === 0}>
              <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('previous')}
            </Button>
            {currentQuestionIndex < examQuestions.length - 1 ? (
              <Button variant="primary" onClick={() => setCurrentQuestionIndex((prev) => prev + 1)} disabled={finalQuizAnswers[finalQuestion?.id] === undefined} className="max-sm:tw-flex-1">
                {label('next')}
                <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
              </Button>
            ) : (
              <Button variant="primary" onClick={submitFinalQuiz} disabled={Object.keys(finalQuizAnswers).length < examQuestions.length} className="max-sm:tw-flex-1">
                {label('finishExam')}
              </Button>
            )}
          </>
        ) : passed ? (
          <>
            <Button onClick={() => { setShowFinalQuiz(false); setShowCertificate(true); }}>
              <LuAward aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('viewCertificate')}
            </Button>
            <Button variant="success" onClick={handleCompleteEducation} className="max-sm:tw-flex-1">
              <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('completeEducation')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setShowFinalQuiz(false)}>
              <LuBookOpen aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('rewatchVideo')}
            </Button>
            <Button variant="primary" onClick={retakeFinalQuiz} className="max-sm:tw-flex-1">
              <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('retake')}
            </Button>
          </>
        )}
      >
        {!showFinalResults ? (
          <div className="tw-space-y-5">
            <div className="tw-space-y-2">
              <p className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('questionOf', { current: currentQuestionIndex + 1, total: examQuestions.length })}</p>
              <span className="tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
                <span className="tw-block tw-h-full tw-rounded-full tw-bg-violet-500 tw-transition-[width] tw-duration-300" style={{ width: `${examQuestions.length ? ((currentQuestionIndex + 1) / examQuestions.length) * 100 : 0}%` }} />
              </span>
            </div>
            <p className="tw-text-lg tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{finalQuestion?.question}</p>
            <div role="radiogroup" aria-label={label('choices')} className="tw-space-y-2">
              {finalQuestion?.options.map((option, index) => (
                <QuizChoice
                  key={index}
                  letter={String.fromCharCode(65 + index)}
                  state={finalQuizAnswers[finalQuestion.id] === index ? 'selected' : 'idle'}
                  onClick={() => handleFinalQuizAnswer(finalQuestion.id, index)}
                >
                  {option}
                </QuizChoice>
              ))}
            </div>
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-4 tw-text-center">
            <span className={`tw-grid tw-h-20 tw-w-20 tw-place-items-center tw-rounded-full tw-text-white tw-shadow-xl ${passed ? 'tw-bg-emerald-500 tw-shadow-emerald-500/30' : 'tw-bg-rose-500 tw-shadow-rose-500/30'}`}>
              {passed ? <LuCircleCheck aria-hidden="true" className="tw-h-10 tw-w-10" /> : <LuCircleX aria-hidden="true" className="tw-h-10 tw-w-10" />}
            </span>
            <p className="tw-text-2xl tw-font-bold tw-text-slate-900 dark:tw-text-white">{passed ? label('congrats') : label('sorry')}</p>
            <p className={`tw-text-4xl tw-font-bold tw-tabular-nums ${passed ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-rose-600 dark:tw-text-rose-300'}`}>{finalQuizScore}%</p>
            <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{label('scoreLabel')}</p>
            <p className="tw-max-w-md tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
              {passed ? label('passedText') : label('failedText', { score: examPassScore })}
            </p>
          </div>
        )}
      </Modal>

      {/* Sertifika */}
      <Modal
        isOpen={showCertificate}
        onClose={() => setShowCertificate(false)}
        size="lg"
        title={label('certificateModalTitle')}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-amber-300 tw-to-amber-500 tw-shadow-amber-500/30" size="xs">
            <LuAward aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
      >
        <div className="tw-rounded-[1.5rem] tw-border-4 tw-border-double tw-border-amber-400/70 tw-bg-gradient-to-br tw-from-amber-50 tw-via-white tw-to-amber-50 tw-p-6 tw-text-center sm:tw-p-10 dark:tw-from-amber-500/[0.08] dark:tw-via-white/[0.02] dark:tw-to-amber-500/[0.08]">
          <LuAward aria-hidden="true" className="tw-mx-auto tw-h-12 tw-w-12 tw-text-amber-500" />
          <p className="tw-mt-2 tw-text-2xl tw-font-bold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('certificateHeading')}</p>
          <p className="tw-mt-6 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('certificateIntro')}</p>
          <p className="tw-mt-1 tw-text-2xl tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">{auth.currentUser?.displayName || label('participant')}</p>
          <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('certificateOf')}</p>
          <p className="tw-mt-3 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">“{education?.title}”</p>
          <p className="tw-mt-2 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{label('certificateScore', { score: finalQuizScore })}</p>
          <div className="tw-mt-8 tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-4 sm:tw-flex-row">
            <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('certificateDate', { date: new Date().toLocaleDateString(locale) })}</span>
            <span className="tw-flex tw-flex-col tw-items-center tw-gap-1">
              <span className="tw-h-px tw-w-40 tw-bg-slate-400/60" />
              <span className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">HabcareCRO {label('qualityManagement')}</span>
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

EducationPlayer.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  education: PropTypes.object,
  onComplete: PropTypes.func,
};

export default EducationPlayer;
